/**
 * After Hours — phase 0 of the daily podcast: the script, its audit, and the
 * prediction ledger. Private to the firm until Alan says otherwise.
 *
 * What this page is FOR: reading the show before it has a voice. Every line
 * carries its speaker and its source ids; the audit panel says why a day did
 * or did not press; the ledger shows every call the show has made and how it
 * was graded. If a line here can't be traced, that is the bug to report.
 */
import { Component, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { API_BASE, api, Manifest, PodcastEpisode, PodcastEpisodeFull, PodcastEpisodePage, ShowPrediction } from "../api";
import Pager from "./Pager";

const PAGE = 25;
const STATUSES = ["published", "rendered", "scripted", "failed"];

const SPEAKER: Record<string, { name: string; cls: string }> = {
  DOC: { name: "Doc Perrin", cls: "text-up" },
  ROCCO: { name: "Rocco Vance", cls: "text-gold" },
  GUEST: { name: "Guest", cls: "text-accent" },
  STONKY: { name: "Stonky", cls: "text-slate-200" },
  DON: { name: "Long Duk Don", cls: "text-down" },
  BARRY: { name: "Barry LaCroix", cls: "text-gold" },
};

const SEGMENT: Record<string, string> = {
  cold_open: "Cold open", headline: "The headline", thread: "Pull the thread", guest: "The guest",
  long_view: "The long view", barry: "Barry LaCroix", follow_ups: "Follow-ups", scoreboard: "Scoreboard", calls: "Today's calls", desk_note: "Desk note", out: "Out",
};

function Status({ s }: { s: string }) {
  const tone = s === "scripted" || s === "rendered" || s === "published" ? "bg-up/20 text-up"
    : s === "failed" ? "bg-down/20 text-down" : s === "preview" ? "bg-gold/20 text-gold" : "bg-slate-800 text-slate-400";
  return <span className={`badge ${tone}`}>{s}</span>;
}

function PredStatus({ s }: { s: string }) {
  const tone = s === "hit" ? "bg-up/20 text-up" : s === "miss" ? "bg-down/20 text-down"
    : s === "void" ? "bg-slate-800 text-slate-500" : "bg-gold/20 text-gold";
  return <span className={`badge ${tone}`}>{s}</span>;
}

class Boundary extends Component<{ children: ReactNode }, { err: string | null }> {
  state = { err: null as string | null };
  static getDerivedStateFromError(e: unknown) { return { err: String(e) }; }
  render() {
    if (this.state.err) {
      return (
        <div className="card text-sm">
          <div className="text-down font-semibold">After Hours could not render.</div>
          <div className="text-slate-400 mt-1 font-mono text-xs break-all">{this.state.err}</div>
          <div className="text-slate-500 mt-2">Usually a stored script with an unexpected shape. The episode rows and the ledger are intact on the server.</div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Podcast(props: { manifest: Manifest }) {
  return <Boundary><PodcastView {...props} /></Boundary>;
}

function PodcastView(_: { manifest: Manifest }) {
  const [page, setPage] = useState<PodcastEpisodePage>({ total: 0, offset: 0, limit: PAGE, items: [] });
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState<PodcastEpisodeFull | null>(null);
  const [preds, setPreds] = useState<ShowPrediction[]>([]);
  const [previews, setPreviews] = useState<PodcastEpisode[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBrief, setShowBrief] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [coverSrc, setCoverSrc] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const player = useRef<HTMLAudioElement | null>(null);

  // The artwork is tenant-authenticated like the audio: fetched once with
  // credentials and kept as a blob URL for the page and the lock screen.
  useEffect(() => {
    let url: string | null = null;
    void (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/podcast/cover.jpg?size=600`, { credentials: "include" });
        if (!r.ok) return;
        url = URL.createObjectURL(await r.blob());
        setCoverSrc(url);
      } catch { /* no cover */ }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, []);

  // What the phone's lock screen and the car show while it plays. A blob
  // <audio> carries no ID3 to the OS; the Media Session API is the only
  // way a browser player names the show (Alan, 2026-09-16: "no author
  // information, show titles or episode description").
  useEffect(() => {
    if (!open || !audioSrc || !("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    // Artwork must be a plain fetchable URL for the lock screen (a blob URL
    // or a cookie-gated one is dropped on iOS), so the public cover is used.
    const name = () => {
      ms.metadata = new MediaMetadata({
        title: `#${open.number || "—"} · ${open.title || open.show_day}`,
        artist: open.artist || "Doc Perrin & Rocco Vance, with Stonky",
        album: open.album || "The Bull Sheet After Hours",
        artwork: [{ src: `${API_BASE}/api/public/podcast/cover.jpg?size=600`, sizes: "600x600", type: "image/jpeg" }],
      });
    };
    name();
    const a = player.current;
    // Re-applied on every state the phone re-reads it in (play, playing,
    // loadedmetadata, durationchange), with the position so the lock-screen
    // scrubber is live.
    const pos = () => {
      const x = player.current;
      if (!x || !isFinite(x.duration)) return;
      try { ms.setPositionState({ duration: x.duration, playbackRate: x.playbackRate || 1, position: x.currentTime }); } catch { /* */ }
    };
    const onPlay = () => { name(); pos(); try { ms.playbackState = "playing"; } catch { /* */ } };
    const onPause = () => { try { ms.playbackState = "paused"; } catch { /* */ } };
    for (const ev of ["play", "playing", "loadedmetadata", "durationchange"]) a?.addEventListener(ev, onPlay);
    a?.addEventListener("timeupdate", pos); a?.addEventListener("seeked", pos);
    a?.addEventListener("pause", onPause);
    const el = () => player.current;
    const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
      ["play", () => { void el()?.play(); }],
      ["pause", () => el()?.pause()],
      ["seekbackward", (d) => { const a = el(); if (a) a.currentTime = Math.max(0, a.currentTime - (d.seekOffset ?? 10)); }],
      ["seekforward", (d) => { const a = el(); if (a) a.currentTime = Math.min(a.duration || Infinity, a.currentTime + (d.seekOffset ?? 10)); }],
      ["seekto", (d) => { const a = el(); if (a && d.seekTime != null) a.currentTime = d.seekTime; }],
    ];
    for (const [action, fn] of handlers) { try { ms.setActionHandler(action, fn); } catch { /* unsupported action */ } }
    return () => {
      for (const ev of ["play", "playing", "loadedmetadata", "durationchange"]) a?.removeEventListener(ev, onPlay);
      a?.removeEventListener("timeupdate", pos); a?.removeEventListener("seeked", pos);
      a?.removeEventListener("pause", onPause);
      for (const [action] of handlers) { try { ms.setActionHandler(action, null); } catch { /* */ } }
    };
  }, [open?.id, open?.title, open?.number, audioSrc]); // eslint-disable-line react-hooks/exhaustive-deps

  // The MP3 is served tenant-authenticated on another origin, so it is
  // fetched with credentials and handed to the player as a blob URL.
  // The player gets a REAL url — a signed, expiring link to the MP3 — not a
  // blob. iOS shows no title and no artwork on the lock screen for blob
  // media whatever Media Session says (Alan, 2026-10-19: "i don't get logo
  // or episode info in my player"), and a real url also gives the phone
  // Range seeking. The blob fetch stays only as the fallback for an API
  // that predates stream_url.
  useEffect(() => {
    let url: string | null = null;
    setAudioSrc(null);
    if (open?.stream_url) {
      setAudioSrc(open.stream_url);
    } else if (open?.audio_url) {
      void (async () => {
        try {
          const r = await fetch(`${API_BASE}${open.audio_url}`, { credentials: "include" });
          if (!r.ok) return;
          url = URL.createObjectURL(await r.blob());
          setAudioSrc(url);
        } catch { /* no audio yet */ }
      })();
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [open?.id, open?.audio_url, open?.stream_url, open?.duration_s]);

  // Safari's own Now Playing falls back to the page title; make it the show.
  useEffect(() => {
    if (!open) return;
    const prev = document.title;
    document.title = `#${open.number || "—"} · ${open.title || open.show_day} — The Bull Sheet After Hours`;
    return () => { document.title = prev; };
  }, [open?.id, open?.title, open?.number]); // eslint-disable-line react-hooks/exhaustive-deps

  const [shareMsg, setShareMsg] = useState<string | null>(null);
  const share = async () => {
    if (!open) return;
    setError(null);
    try {
      const r = await api.post<{ share_url: string; feed_url: string }>(`/api/podcast/episodes/${open.id}/share`, {});
      setOpen({ ...open, share_url: r.share_url, status: "published" });
      try { await navigator.clipboard.writeText(r.share_url); setShareMsg("Link copied"); } catch { setShareMsg("Link ready"); }
      await loadList();
    } catch (e) { setError(String(e)); }
  };
  const unshare = async () => {
    if (!open) return;
    try {
      await api.del(`/api/podcast/episodes/${open.id}/share`);
      setOpen({ ...open, share_url: null, status: "rendered" }); setShareMsg(null);
      await loadList();
    } catch (e) { setError(String(e)); }
  };

  const render = async () => {
    if (!open) return;
    setRendering(true); setError(null);
    try {
      await api.post(`/api/podcast/episodes/${open.id}/render`, {});
      setOpen(await api.get<PodcastEpisodeFull>(`/api/podcast/episodes/${open.id}`));
      await loadList();
    } catch (e) { setError(String(e)); } finally { setRendering(false); }
  };

  const listQs = new URLSearchParams({
    limit: String(PAGE), offset: String(offset),
    ...(q ? { q } : {}), ...(status ? { status } : {}),
  }).toString();
  const loadList = useCallback(async () => {
    const p = await api.get<PodcastEpisodePage>(`/api/podcast/episodes?${listQs}`);
    setPage(p);
    return p;
  }, [listQs]);

  useEffect(() => { loadList().catch((e) => setError(String(e))); }, [loadList]);
  useEffect(() => {
    api.get<ShowPrediction[]>("/api/podcast/predictions").then(setPreds).catch((e) => setError(String(e)));
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { setQ(query.trim()); setOffset(0); }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const press = async () => {
    setBusy(true); setError(null);
    try {
      const ep = await api.post<PodcastEpisodeFull>("/api/podcast/press?force=true", {});
      setOpen(ep);
      await loadList();
      setPreds(await api.get<ShowPrediction[]>("/api/podcast/predictions"));
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    api.get<PodcastEpisode[]>("/api/podcast/previews").then(setPreviews).catch(() => setPreviews([]));
  }, []);

  const pickPreview = async (name: string) => {
    setOpen(await api.get<PodcastEpisodeFull>(`/api/podcast/previews/${name}`));
    setShowBrief(false);
  };

  const pick = async (id: string) => {
    setOpen(await api.get<PodcastEpisodeFull>(`/api/podcast/episodes/${id}`));
    setShowBrief(false);
  };

  useEffect(() => {
    if (open || page.items.length === 0) return;
    const first = page.items.find((e) => e.status !== "failed") ?? page.items[0];
    void pick(first.id);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const failed = open?.audit?.checks?.filter((c) => !c.ok) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">The Bull Sheet After Hours</h1>
          <p className="text-sm text-slate-400 max-w-2xl">
            The daily show's script, before it has a voice. Every line names its speaker and its sources;
            the audit says why a day pressed or didn't. Private to the firm.
          </p>
        </div>
        <button className="btn" onClick={press} disabled={busy}>{busy ? "Writing…" : "Write today's script"}</button>
      </div>
      {error && <div className="text-down text-sm">{error}</div>}

      <div className="space-y-4">
        {previews.length > 0 && (
          <div className="card space-y-2">
            <div>
              <h2 className="label">Previews</h2>
              <p className="text-xs text-slate-500">Rendered for your ear only. Not episodes: never shared, never in the feed or the email; bets in them are not filed.</p>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {previews.map((e) => (
                  <tr key={e.id} onClick={() => e.preview && pickPreview(e.preview)}
                    className={`cursor-pointer border-b border-edge/60 hover:bg-slate-800/40 ${open?.id === e.id ? "bg-slate-800/60" : ""}`}>
                    <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">{e.show_day}</td>
                    <td className="py-1.5 pr-3 font-semibold min-w-[14rem]">
                      {e.title}
                      {e.barry && <span className="ml-2 text-xs font-normal text-gold">with Barry LaCroix ({e.barry.night})</span>}
                    </td>
                    <td className="py-1.5 pr-3"><Status s={e.status} /></td>
                    <td className="py-1.5 pr-3 text-xs text-slate-500 whitespace-nowrap">
                      {e.duration_s != null ? `${Math.floor(e.duration_s / 60)}:${String(e.duration_s % 60).padStart(2, "0")}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="card space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="label mr-auto">Episodes</h2>
            <input className="input w-64" placeholder="Search title, date, headline, guest" value={query}
              onChange={(e) => setQuery(e.target.value)} />
            <select className="input w-auto" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
              <option value="">All statuses</option>
              {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          {page.items.length === 0 && (
            <div className="text-sm text-slate-500">
              {page.total === 0 && !q && !status
                ? "Nothing written yet. The scheduler presses at 17:50 ET after the evening edition."
                : "No episodes match these filters."}
            </div>
          )}
          {page.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-edge">
                    <th className="py-1.5 pr-3 font-medium">#</th>
                    <th className="py-1.5 pr-3 font-medium">Day</th>
                    <th className="py-1.5 pr-3 font-medium">Title</th>
                    <th className="py-1.5 pr-3 font-medium">Status</th>
                    <th className="py-1.5 pr-3 font-medium">Length</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((e) => (
                    <tr key={e.id} onClick={() => pick(e.id)}
                      className={`cursor-pointer border-b border-edge/60 hover:bg-slate-800/40 ${open?.id === e.id ? "bg-slate-800/60" : ""}`}>
                      <td className="py-1.5 pr-3 font-mono text-xs text-slate-500">{e.number || "—"}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">{e.show_day}</td>
                      <td className="py-1.5 pr-3 font-semibold min-w-[14rem]">
                        {e.title || "(untitled)"}
                        {e.guest && <span className="ml-2 text-xs font-normal text-slate-500">{e.guest.replace("board.", "")}</span>}
                      </td>
                      <td className="py-1.5 pr-3"><Status s={e.status} /></td>
                      <td className="py-1.5 pr-3 text-xs text-slate-500 whitespace-nowrap">
                        {e.duration_s != null
                          ? `${Math.floor(e.duration_s / 60)}:${String(e.duration_s % 60).padStart(2, "0")}`
                          : e.audit ? `${e.audit.words} words · ~${e.audit.est_minutes} min` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager offset={page.offset} limit={page.limit} total={page.total} onOffset={setOffset} />
        </div>

        <div className="space-y-4">
          {open ? (
            <>
              <div className="card">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold">{open.title}</h2>
                  <Status s={open.status} />
                  <span className="text-xs text-slate-500 font-mono">{open.show_day}</span>
                  {open.guest && <span className="badge bg-slate-800 text-slate-400">guest: {open.guest.replace("board.", "")}</span>}
                </div>
                {open.description && <p className="text-sm text-slate-300 mt-2 max-w-2xl">{open.description}</p>}
                {open.lead_headline && <div className="text-xs text-slate-500 mt-1">Lead: {open.lead_headline}</div>}
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  {coverSrc && <img src={coverSrc} alt="After Hours" className="w-20 h-20 rounded-lg shrink-0 object-cover" />}
                  {audioSrc ? (
                    <audio ref={player} controls playsInline src={audioSrc} className="w-full max-w-xl" preload="metadata" />
                  ) : open.audio_url ? (
                    <span className="text-xs text-slate-500">loading audio…</span>
                  ) : (
                    <span className="text-xs text-slate-500">no voice yet</span>
                  )}
                  {open.duration_s != null && <span className="text-xs font-mono text-slate-500">{Math.floor(open.duration_s / 60)}:{String(open.duration_s % 60).padStart(2, "0")}</span>}
                  {Array.isArray(open.script?.lines) && open.status !== "failed" && open.status !== "preview" && (
                    <button className="btn" onClick={render} disabled={rendering}>
                      {rendering ? "Rendering…" : open.audio_url ? "Re-render voice" : "Render voice"}
                    </button>
                  )}
                  {open.audio_url && !open.share_url && <button className="btn" onClick={share}>Share publicly</button>}
                </div>
                {open.barry?.bet && (
                  <div className="mt-3 text-sm border border-gold/30 rounded-lg p-3 max-w-2xl">
                    <div className="label text-gold">Barry's long shot · {open.barry.night}{open.status === "preview" ? " · not filed (preview)" : ""}</div>
                    <div className="font-mono">
                      {open.barry.bet.symbol} long · entry {open.barry.bet.entry} · target {open.barry.bet.target} · stop {open.barry.bet.stop} · {open.barry.bet.horizon_days} sessions
                    </div>
                    <div className="text-xs text-slate-400">
                      house {open.barry.bet.odds?.house_touch_pct ?? "?"}% to touch · pays {open.barry.bet.odds?.payout ?? "?"} to 1 · breakeven {open.barry.bet.odds?.breakeven_pct ?? "?"}%
                      {open.barry.bet.barry_pct != null && <> · Barry says {open.barry.bet.barry_pct}%</>}
                    </div>
                    {open.barry.bet.thesis && <div className="text-slate-300 mt-1">{open.barry.bet.thesis}</div>}
                  </div>
                )}
                {open.share_url && (
                  <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
                    <span className="text-slate-500">Public link:</span>
                    <a className="text-gold hover:underline break-all" href={open.share_url} target="_blank" rel="noreferrer">{open.share_url}</a>
                    {shareMsg && <span className="text-up">{shareMsg}</span>}
                    <button className="text-down hover:underline" onClick={unshare}>revoke</button>
                  </div>
                )}
                <div className="mt-3">
                  <h3 className="label">Audit {open.audit?.ok ? "· passed" : "· FAILED"}</h3>
                  <ul className="text-sm space-y-1">
                    {(open.audit?.checks ?? []).map((c) => (
                      <li key={c.name} className="flex gap-2">
                        <span className={c.ok ? "text-up" : "text-down"}>{c.ok ? "✓" : "✗"}</span>
                        <span className="font-mono text-xs text-slate-400 w-56 shrink-0">{c.name}</span>
                        <span className={c.ok ? "text-slate-400" : "text-down"}>{c.detail}</span>
                      </li>
                    ))}
                  </ul>
                  {failed.length > 0 && (
                    <div className="text-xs text-down mt-2">This day did not press. The script below is what the writer produced and is kept for the audit trail only.</div>
                  )}
                </div>
              </div>

              {Array.isArray(open.script?.lines) && (
                <div className="card">
                  <h2 className="label">Transcript</h2>
                  <div className="space-y-2 text-sm">
                    {open.script.lines.map((raw, i) => {
                      // A failed press can store a cut-off script; a line may be
                      // missing fields or not be an object at all. Render what is
                      // there — the audit panel above says why the day failed.
                      const l = { speaker: String(raw?.speaker ?? "?"), text: String(raw?.text ?? ""),
                                  segment: raw?.segment ?? undefined };
                      const sp = SPEAKER[l.speaker] ?? { name: l.speaker, cls: "text-slate-300" };
                      const prevRaw = open.script!.lines[i - 1];
                      const newSeg = !prevRaw || prevRaw?.segment !== l.segment;
                      return (
                        <div key={i}>
                          {newSeg && l.segment && <div className="label mt-4 mb-1">{SEGMENT[l.segment] ?? l.segment}</div>}
                          <div className="grid grid-cols-[110px_1fr] gap-2">
                            <span className={`font-semibold ${sp.cls}`}>{sp.name}</span>
                            <span className="text-slate-200">
                              {l.text.split(/(\[\[S\d+\]\])/).map((part, j) => {
                                const m = part.match(/^\[\[(S\d+)\]\]$/);
                                if (!m) return <span key={j}>{part}</span>;
                                const src = open.manifest?.find((r) => r.id === m[1]);
                                return (
                                  <sup key={j} className="text-accent ml-0.5" title={src ? `${src.label}${src.url ? " — " + src.url : ""}` : m[1]}>
                                    {src?.url ? <a href={src.url} target="_blank" rel="noreferrer">{m[1]}</a> : m[1]}
                                  </sup>
                                );
                              })}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="card">
                  <h2 className="label">Today's calls</h2>
                  {(open.predictions ?? []).length === 0 && <div className="text-sm text-slate-500">none logged</div>}
                  {(open.predictions ?? []).map((p) => (
                    <div key={p.id} className="border-t border-edge py-2 text-sm first:border-t-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{p.symbol} {p.comparison} {p.threshold}</span>
                        <span className="text-xs text-slate-500">by {p.horizon_date}</span>
                        <span className="ml-auto"><PredStatus s={p.status} /></span>
                      </div>
                      <div className="text-slate-300">{p.claim}</div>
                      <div className="text-xs text-slate-500">Wrong if: {p.falsifier}</div>
                      {p.graded_value != null && <div className="text-xs font-mono">graded {p.graded_value}</div>}
                    </div>
                  ))}
                </div>
                <div className="card">
                  <h2 className="label">Proposed for the graph</h2>
                  {(open.script?.deltas ?? []).length === 0 && <div className="text-sm text-slate-500">no proposals this episode</div>}
                  {(Array.isArray(open.script?.deltas) ? open.script!.deltas! : []).filter((d) => d && typeof d === "object").map((d, i) => (
                    <div key={i} className="border-t border-edge py-2 text-sm first:border-t-0">
                      <div className="font-mono text-xs text-slate-500">{d.kind}{d.source ? ` · ${d.source}` : ""}</div>
                      <div className="font-semibold">{d.kind === "edge" ? `${d.from} —${d.relation}→ ${d.to}` : d.name}</div>
                      <div className="text-slate-400">{d.rationale}</div>
                    </div>
                  ))}
                  {open.fock && (
                    <div className="text-xs text-slate-500 mt-2">
                      Graph walk: {open.fock.hits?.length ?? 0} hits, {open.fock.edges?.length ?? 0} edges
                      {open.fock.error ? ` · ${open.fock.error}` : ""}
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <button className="label" onClick={() => setShowBrief((v) => !v)}>
                  {showBrief ? "▾" : "▸"} The brief the writer saw
                </button>
                {showBrief && <pre className="text-xs whitespace-pre-wrap text-slate-400 mt-2 max-h-[480px] overflow-auto">{open.brief_text}</pre>}
              </div>
            </>
          ) : (
            <div className="card text-sm text-slate-500">Pick an episode, or write today's script.</div>
          )}
        </div>
      </div>

      <div className="card">
        <h2 className="label">The ledger — every call the show has made</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead><tr className="text-left text-xs text-slate-500 uppercase">
              <th className="py-1">Day</th><th>Who</th><th>Call</th><th>By</th><th>Status</th><th>Graded</th></tr></thead>
            <tbody>
              {preds.map((p) => (
                <tr key={p.id} className="border-t border-edge">
                  <td className="py-1">{p.show_day}</td><td>{SPEAKER[p.speaker]?.name ?? p.speaker}</td>
                  <td>{p.symbol} {p.comparison} {p.threshold}</td><td>{p.horizon_date}</td>
                  <td><PredStatus s={p.status} /></td><td>{p.graded_value ?? "—"}</td>
                </tr>
              ))}
              {preds.length === 0 && <tr><td colSpan={6} className="py-2 text-slate-500">no calls yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
